import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  signOut,
  fetchAuthSession,
  updateUserAttributes,
  signIn,
  getCurrentUser,
  updatePassword,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { useAuth } from "../context/AuthProvider";
import { modal } from "../modal/ModalService";
import { isValidPassword, LoadingElement, toE164Kr } from "../helpers/utils";
import _ from "lodash";
import {
  KAKAO,
  LOG_IN_COOKIE,
  MY_SHOP_URL_PATH,
  NAVER,
  REST_API_URL,
} from "../constants";
import API from "../service/API";
import { deleteCookie, getCookie, setCookie } from "../helpers/cookie";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";
import logoImage from "../img/granhand_logo.png";

const PasswordMode = React.memo(
  ({
    passwords,
    setPasswords,
    onPasswordSave,
    onCancel,
    busy,
    i18n,
    errorMessage,
    setErrorMessage,
  }) => {
    const savable = passwords?.current && passwords?.new && passwords?.re;

    return (
      <section className="signup-auth__step">
        <form className="signup-auth__form">
          <div className="signup-auth__field">
            <label className="signup-auth__label" htmlFor="password-current">
              {i18n("mypage.password.current.label")}
            </label>
          <input
            id="password-current"
            type="password"
            className="signup-auth__input"
            placeholder={i18n("mypage.password.current.placeholder")}
            value={passwords?.current || ""}
            onChange={(e) =>
              {
                setPasswords({ ...passwords, current: e.target.value });
                setErrorMessage("");
              }
            }
            autoComplete="current-password"
          />
          </div>

          <div className="signup-auth__field">
            <label className="signup-auth__label" htmlFor="password-new">
              {i18n("mypage.password.new.label")}
            </label>
          <input
            id="password-new"
            type="password"
            className="signup-auth__input"
            placeholder={i18n("mypage.password.new.placeholder")}
            value={passwords?.new || ""}
            onChange={(e) =>
              {
                setPasswords({ ...passwords, new: e.target.value });
                setErrorMessage("");
              }
            }
            autoComplete="new-password"
          />
          </div>

          <div className="signup-auth__field">
            <label className="signup-auth__label" htmlFor="password-re">
              {i18n("mypage.password.confirm.label")}
            </label>
          <input
            id="password-re"
            type="password"
            className="signup-auth__input"
            placeholder={i18n("mypage.password.confirm.placeholder")}
            value={passwords?.re || ""}
            onChange={(e) => {
              setPasswords({ ...passwords, re: e.target.value });
              setErrorMessage("");
            }}
            autoComplete="new-password"
          />
          </div>

          {errorMessage ? (
            <div className="auth-inline-error" role="alert" aria-live="polite">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <circle
                  cx="8"
                  cy="8"
                  r="5.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M8 5.1v3.3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="8" cy="10.95" r="0.8" fill="currentColor" />
              </svg>
              <span>{errorMessage}</span>
            </div>
          ) : null}

          <div className="mypage-password__actions">
            <button
              type="button"
              onClick={onPasswordSave}
              disabled={!savable || busy}
              aria-disabled={!savable || busy}
              className="signup-auth__submit"
            >
              {i18n("common.ok")}
            </button>

            <button
              type="button"
              className="signup-auth__outline-button"
              disabled={busy}
              onClick={onCancel}
            >
              {i18n("mypage.password.cancel")}
            </button>
          </div>
        </form>
      </section>
    );
  }
);

const AuthoizeRender = React.memo(
  ({
    attrs,
    onAuthoize,
    setPassword,
    password,
    busy,
    isSocial,
    i18n,
    onKeyDown,
  }) => {
    return !isSocial ? (
      <section className="signup-auth__step">
        <div className="signup-auth__summary">
          <p className="signup-auth__summary-title">
            {i18n("mypage.auth.recheck.title")}
          </p>
          <p className="signup-auth__summary-desc">
            {i18n("mypage.auth.recheck.desc")}
          </p>
        </div>

        <form className="signup-auth__form">
          <div className="signup-auth__field">
            <label className="signup-auth__label" htmlFor="email">
              {i18n("signup.form.email.label")}
            </label>
          <input
            id="email"
            type="email"
            className="signup-auth__input"
            placeholder={i18n("signup.form.email.placeholder")}
            value={attrs.email}
            disabled
            aria-disabled={true}
            autoComplete="email"
          />
          </div>

          <div className="signup-auth__field">
            <label className="signup-auth__label" htmlFor="password">
              {i18n("signup.form.password.label")}
            </label>
          <input
            id="password"
            type="password"
            className="signup-auth__input"
            placeholder={i18n("signin.form.password.placeholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            onKeyDown={onKeyDown}
          />
          </div>

          <button
            type="button"
            onClick={onAuthoize}
            disabled={!password || busy}
            aria-disabled={!password || busy}
            className="signup-auth__submit"
          >
            {i18n("common.ok")}
          </button>
        </form>
      </section>
    ) : (
      <section className="signup-auth__step">
        <div className="signup-auth__summary">
          <p className="signup-auth__summary-title">
            {i18n("mypage.auth.loginRecheck.title")}
          </p>
          <p className="signup-auth__summary-desc">
            {i18n("mypage.auth.loginRecheck.desc")}
          </p>
        </div>

        <form className="signup-auth__form">
          <button
            type="button"
            onClick={onAuthoize}
            disabled={!isSocial && !password}
            aria-disabled={!isSocial && !password}
            className="signup-auth__outline-button"
          >
            {i18n(`social.${isSocial?.toLowerCase() || ""}`)}
            {i18n("mypage.auth.social.verify")}
          </button>
        </form>
      </section>
    );
  }
);

export default function MyPage() {
  const { user, loading } = useAuth();
  const { i18n, cafe24RedirectUrl } = useLang();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  // TOFIX:
  const [confirmed, setConfirmed] = useState(false);
  const [password, setPassword] = useState('');
  const [isEdit, setEdit] = useState(false);
  const [editing, setEditing] = useState({});
  const [passwordMode, setPasswordMode] = useState(false);
  const [passwords, setPasswords] = useState({});
  const [passwordError, setPasswordError] = useState("");
  const [account, setAccount] = useState(_.cloneDeep(user));

  const isSocial = account?.attributes?.["custom:provider"];
  let attrs = account?.attributes || {};

  const userKey = user?.sub;
  useEffect(() => {
    setConfirmed(false);
  }, [userKey]);

  const refresh = async () => {
    try {
      const me = await getCurrentUser();
      await fetchAuthSession({ forceRefresh: true });
      const attrs = await fetchUserAttributes();
      setAccount((u) => ({ ...u, ...me, attributes: attrs }));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO.API_KEY);
    }
    getKakaoSession();
    getNaverSession();
    return () => {
      delete window.result;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const s = await fetchAuthSession();
      const ph = s.tokens?.idToken?.payload?.phone_number || "";
      const em = s.tokens?.idToken?.payload?.email || "";
    } catch (e) {
      modal.alert({
        message: e?.message || String(e),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      payload.name = editing.name;
      payload.birthdate = editing.birthdate;

      await updateUserAttributes({ userAttributes: payload });
      await API.syncUser(user.username);
      await modal.alert({
        message: i18n("mypage.profile.update.success"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
      await refresh();
      await load();
    } catch (err) {
      modal.alert({
        message: err?.message || String(err),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    } finally {
      setEdit(false);
      setEditing({});
      setBusy(false);
    }
  };

  const handlePasswordChangeTry = () => {
    setPasswordError("");
    setPasswordMode(true);
  };

  const handlePasswordSave = async () => {
    if (!passwords.current || !passwords.new || !passwords.re) return;
    if (!isValidPassword(passwords.new)) {
      setPasswordError(i18n("mypage.password.validation.rule"));
      return;
    }
    if (passwords.new !== passwords.re) {
      setPasswordError(i18n("mypage.password.validation.mismatch"));
      return;
    }

    try {
      await updatePassword({
        oldPassword: passwords.current,
        newPassword: passwords.new,
      });
      modal.alert({
        message: i18n("mypage.password.update.success"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
      setPasswords({});
      setPasswordError("");
      setPasswordMode(false);
    } catch (error) {
      if (error?.name === "NotAuthorizedException") {
        setPasswordError(i18n("auth.password.incorrect"));
      } else {
        setPasswordError(i18n("mypage.password.update.fail"));
      }
    }
  };

  const handleBack = async () => {
    window.location.replace(cafe24RedirectUrl + MY_SHOP_URL_PATH);
  };

  const handleSignOutTry = async () => {
    const ok = await modal.confirm({
      message: i18n("signin.confirm.signout"),
      okText: i18n("common.ok"),
      cancelText: i18n("common.cancel"),
      backdropClose: false,
    });
    if (ok) {
      onSignOut();
    }
  };

  const handleDropoutTry = () => {
    nav("/drop-out");
  };

  const onSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
      await refresh();
      nav("/");
    } catch (e) {
      modal.alert({
        message: e?.message || String(e),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleAuthorize();
    }
  };

  const handleAuthorize = async () => {
    const _username = _.cloneDeep(user.username);
    setBusy(true);
    try {
      await getCurrentUser();
      await signOut();
    } catch {}
    try {
      await signIn({ username: _username, password });
      setConfirmed(true);
      setBusy(false);
    } catch (error) {
      setBusy(false);
      modal.alert({
        message: i18n("auth.password.incorrect"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    }
  };

  const getKakaoSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "kakao") {
      fetch(`${REST_API_URL}${KAKAO.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            deleteCookie(LOG_IN_COOKIE);
            setConfirmed(true);
          }
        });
    }
  };

  const getNaverSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "naver") {
      fetch(`${REST_API_URL}${NAVER.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            deleteCookie(LOG_IN_COOKIE);
            setConfirmed(true);
          }
        });
    }
  };

  const handleSocialAuthorize = () => {
    if (isSocial === "KAKAO") {
      if (!window.Kakao) return;
      setCookie(LOG_IN_COOKIE, "kakao");
      window.Kakao.Auth.authorize({
        redirectUri: `${REST_API_URL}${KAKAO.CALLBACK_PATH}`,
      });
    } else {
      setCookie(LOG_IN_COOKIE, "naver");
      const redirectUri = `${REST_API_URL}${NAVER.CALLBACK_PATH}`;
      const state = crypto.randomUUID();

      const naverAuthUrl = `${NAVER.AUTH_URL}?response_type=code&client_id=${
        NAVER.CLIENT_ID
      }&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;

      window.location.href = naverAuthUrl;
    }
  };

  const handleEditPress = () => {
    setEditing(_.cloneDeep(attrs));
    setEdit(true);
  };

  if (loading || !user) return <LoadingElement />;

  return (
    <main className="signup-auth mypage-auth container">
      <div className="signup-auth__frame">
        <div className="signup-auth__toolbar">
          <button
            type="button"
            className="signup-auth__back"
            onClick={handleBack}
            aria-label={i18n("mypage.nav.backShop.aria")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{i18n("mypage.nav.backShop")}</span>
          </button>
          <div className="signup-auth__lang">
            <LanguageDropdown />
          </div>
        </div>

        <div className="signup-auth__brand">
          <img alt="GRANHAND." src={logoImage} />
        </div>

        <section className="signup-auth__content">
          {confirmed ? (
        passwordMode ? (
          <PasswordMode
            setPasswords={setPasswords}
            passwords={passwords}
            onPasswordSave={handlePasswordSave}
            busy={busy}
            i18n={i18n}
            errorMessage={passwordError}
            setErrorMessage={setPasswordError}
            onCancel={() => {
              setPasswords({});
              setPasswordError("");
              setPasswordMode(false);
            }}
          />
        ) : (
          <section className="signup-auth__step">
            <div className="mypage-auth__headline">
              <p className="mypage-auth__email">{attrs.email || "email"}</p>
              {isSocial ? (
                <p className="mypage-auth__social-copy">
                  {i18n(`social.${isSocial?.toLowerCase() || ""}`)}
                  {i18n("mypage.social.loggedInSuffix")}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handlePasswordChangeTry}
                  className="mypage-auth__mini-button"
                >
                  {i18n("mypage.password.change")}
                </button>
              )}
            </div>

            <form className="signup-auth__form">
              <p className="mypage-auth__section-title">
                {i18n("mypage.profile.title")}
              </p>

              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="name">
                  {i18n("mypage.profile.name.label")}
                </label>
                <input
                  id="name"
                  className="signup-auth__input mypage-auth__input--readonly"
                  placeholder={i18n("mypage.profile.name.placeholder")}
                  value={attrs?.name}
                  disabled
                  aria-disabled={true}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                />
              </div>

              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="birth">
                  {i18n("signup.form.birth.label")}
                </label>
                <input
                  id="birth"
                  type={isEdit ? "date" : "text"}
                  className={`signup-auth__input ${
                    !isEdit ? "mypage-auth__input--readonly" : ""
                  }`}
                  placeholder={i18n("signup.form.birth.placeholder")}
                  disabled={!isEdit}
                  aria-disabled={!isEdit}
                  value={
                    isEdit
                      ? editing.birthdate
                      : (attrs.birthdate || "").replaceAll("-", ".")
                  }
                  onChange={(e) =>
                    setEditing({ ...editing, birthdate: e.target.value })
                  }
                />
              </div>

              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="phone">
                  {i18n("mypage.profile.phone.label")}
                </label>
                <input
                  id="phone"
                  type="tel"
                  className="signup-auth__input mypage-auth__input--readonly"
                  value={attrs.phone_number}
                  disabled
                  aria-disabled={true}
                />
              </div>

              <div className="mypage-auth__actions">
                <button
                  type="button"
                  onClick={isEdit ? handleUpdate : handleEditPress}
                  disabled={
                    isEdit && (!editing?.birthdate || !editing?.name || busy)
                  }
                  aria-disabled={
                    isEdit && (!editing?.birthdate || !editing?.name || busy)
                  }
                  className="signup-auth__submit"
                >
                  {isEdit
                    ? i18n("mypage.profile.save")
                    : i18n("mypage.profile.edit")}
                </button>

                <button
                  type="button"
                  className="signup-auth__outline-button"
                  onClick={handleDropoutTry}
                >
                  {i18n("mypage.dropout")}
                </button>
              </div>
            </form>
          </section>
        )
      ) : (
        <AuthoizeRender
          user={user}
          attrs={attrs}
          onAuthoize={isSocial ? handleSocialAuthorize : handleAuthorize}
          password={password}
          setPassword={setPassword}
          isSocial={isSocial}
          onKeyDown={handleKeyDown}
          i18n={i18n}
          busy={busy}
        />
      )}
        </section>
      </div>
    </main>
  );
}
